/**
 * The `permanent` filter set for the Area tab of `/tina/audits`.
 *
 * Every entry is a real `GET /area-audit/findings` param — `status`, `kind`, `severity`,
 * `city` — so the whole tab is server-side: the rows, the `total` the pagination footer
 * reads, and the narrowing. Same conventions as `buildVenueAuditFilters`, which this
 * mirrors plus `city`:
 *
 * - **`status` is always a real value**, so a restored view can never look filtered while
 *   it is not.
 * - **`kind`, `severity` and `city` are always PRESENT, carrying `value: undefined` when
 *   cleared**, so a present-but-undefined entry shadows a stale copy in Refine's internal
 *   filter state; the data provider's `flattenFilters` drops it before the wire.
 * - **`city` is the city NAME alone** — the route filters on `city`, not on a city and
 *   country pair, so two same-named cities in different countries share one view.
 */
import type { CrudFilters } from "@refinedev/core";

import type {
  AreaAuditKind,
  AreaAuditSeverity,
  AreaAuditStatus,
} from "@/types/tina";

export interface AreaAuditFilterState {
  /** Which view of the queue: open, dismissed, resolved, or everything. */
  status: AreaAuditStatus;
  /** The count strip's kind; `null` is "any kind". */
  kind: AreaAuditKind | null;
  /** The severity select; `null` is "any severity". */
  severity: AreaAuditSeverity | null;
  /** The city picker's city name; `null` is "all cities". */
  city: string | null;
}

export const buildAreaAuditFilters = ({
  status,
  kind,
  severity,
  city,
}: AreaAuditFilterState): CrudFilters =>
  [
    { field: "status", operator: "eq", value: status },
    { field: "kind", operator: "eq", value: kind ?? undefined },
    { field: "severity", operator: "eq", value: severity ?? undefined },
    { field: "city", operator: "eq", value: city || undefined },
  ] as CrudFilters;
